import { useMemo } from 'react';
import {
  useVisitsInRange,
  type CustomRangeBounds,
  type RangeKey,
} from '@busy/api';
import { useT } from '../../lib/i18n';
import { aggregateTopMenuItems, type TopMenuItem } from '../../lib/topMenuItems';

// Dashboard "Most Requested in this range" tile. Top 3 services + Top 3
// add-ons by visit count, with revenue attribution.
//
// Same gold-crown vocabulary the cut-timer pickers + client profile use
// — across surfaces, the gold crown ALWAYS means "this is the favorite."
// The #1 row in each list gets the gold border + crown; #2 and #3 are
// flatter so the eye lands on the winner first.
//
// Revenue attribution policy lives in lib/topMenuItems.ts: service
// revenue is visit.price_cents minus the sum of add-on prices, so a
// "$50 cut + $5 hot towel" splits as $50 to the cut, $5 to the add-on.

export function TopMenuItemsTile({
  range,
  customBounds,
}: {
  range: RangeKey;
  customBounds?: CustomRangeBounds | null;
}) {
  const t = useT();
  const visits = useVisitsInRange(range, customBounds);

  const agg = useMemo(
    () => aggregateTopMenuItems(visits.data ?? []),
    [visits.data],
  );

  // Hide outright when there's nothing meaningful to show. Single-visit
  // rosters in the today range would render mostly empty columns —
  // better to disappear than render a 1-row tile.
  const hasSignal = agg.services.length > 0 || agg.addons.length > 0;
  if (visits.loading || !hasSignal) return null;

  const rangeLabel = t(`range.${range}`);

  return (
    <section className="rounded-2xl border border-line bg-surface p-4 space-y-4">
      <div className="flex items-baseline justify-between gap-3 flex-wrap">
        <h2 className="font-display text-xl leading-tight">
          {t('dash.topMenu.title')}
        </h2>
        <span className="text-[11px] tracking-[0.14em] uppercase font-extrabold text-gold-500">
          {rangeLabel}
        </span>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Column
          eyebrow={t('dash.topMenu.servicesEyebrow')}
          empty={t('dash.topMenu.servicesEmpty')}
          items={agg.services}
        />
        <Column
          eyebrow={t('dash.topMenu.addonsEyebrow')}
          empty={t('dash.topMenu.addonsEmpty')}
          items={agg.addons}
        />
      </div>
    </section>
  );
}

function Column({
  eyebrow,
  empty,
  items,
}: {
  eyebrow: string;
  empty: string;
  items: TopMenuItem[];
}) {
  const t = useT();
  return (
    <div>
      <div className="text-[11px] tracking-[0.14em] uppercase font-extrabold text-ink-500 mb-2">
        {eyebrow}
      </div>
      {items.length === 0 ? (
        <div className="rounded-xl border border-dashed border-line p-4 text-center text-sm text-ink-500">
          {empty}
        </div>
      ) : (
        <ul className="space-y-2">
          {items.map((item, i) => {
            const isTop = i === 0;
            const countLabel =
              item.count === 1
                ? t('dash.topMenu.timesUsed_one')
                : t('dash.topMenu.timesUsed_other', { n: item.count });
            const dollars = (item.revenueCents / 100).toFixed(
              item.revenueCents % 100 === 0 ? 0 : 2,
            );
            return (
              <li
                key={`${item.serviceId ?? ''}|${item.normalizedName}|${i}`}
                className={`rounded-xl border p-3 flex items-center gap-3 ${
                  isTop
                    ? 'border-gold-500/70 bg-gradient-to-br from-gold-500/10 to-transparent'
                    : 'border-line bg-bg-alt'
                }`}
              >
                {/* Rank chip — gold #1 wears the crown, #2/#3 just show
                    their number. Keeps the eye on the leader without
                    making 2nd/3rd look losing. */}
                <div
                  className={`w-9 h-9 rounded-lg grid place-items-center shrink-0 ${
                    isTop
                      ? 'bg-gold-500 text-navy-900'
                      : 'bg-surface text-ink-500'
                  }`}
                >
                  {isTop ? (
                    <CrownIcon className="w-4 h-4" />
                  ) : (
                    <span className="font-display text-base font-extrabold">{i + 1}</span>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <strong className="block font-display text-base truncate leading-tight">
                    {item.displayName || t('dash.topMenu.unnamed')}
                  </strong>
                  <span className="text-[11px] text-ink-500">{countLabel}</span>
                </div>
                <div className="text-right shrink-0">
                  <div className="font-display text-base text-navy-900 dark:text-gold-500 tabular-nums leading-tight">
                    ${dollars}
                  </div>
                  <div className="text-[10px] uppercase tracking-[0.08em] text-ink-500 font-bold">
                    {t('dash.topMenu.revenue')}
                  </div>
                  {/* Tip attribution — services only. Shows dollar +
                      rate %, so the barber can compare which services
                      tip best across different price points. Empty /
                      zero-tip rows skip this line entirely. */}
                  {item.tipCents > 0 && (
                    <div className="text-[10px] font-bold text-green-600 dark:text-green-400 tabular-nums mt-0.5">
                      {item.tipRatePct != null
                        ? t('dash.topMenu.tipsWithRate', {
                            amount: `$${(item.tipCents / 100).toFixed(0)}`,
                            pct: item.tipRatePct,
                          })
                        : t('dash.topMenu.tips', {
                            amount: `$${(item.tipCents / 100).toFixed(0)}`,
                          })}
                    </div>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

function CrownIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor" stroke="currentColor" strokeWidth="1" strokeLinejoin="round" aria-hidden>
      <path d="M2 19h20l-2 2H4l-2-2z" />
      <path d="M3 7l4 5 5-8 5 8 4-5v10H3V7z" />
    </svg>
  );
}
